<template>
  <div>
    <h1>District Analytics</h1>
    <p class="text-muted font-weight-bold">Cross-school performance analysis</p>
    <hr />

    <!-- Stats Row -->
    <b-row class="mb-2 hover-card">
      <b-col
        md="6"
        lg="3"
        v-for="(stat, index) in stats"
        :key="index"
        class="mb-1 mb-lg-0"
      >
        <b-card no-body class="h-100 cursor-pointer shadow-sm">
          <b-card-body
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <h6 class="mb-50">{{ stat.title }}</h6>
              <h2
                class="font-weight-bolder mb-0"
                :class="stat.valueClass ? stat.valueClass : 'text-dark'"
              >
                {{ stat.value }}
              </h2>
            </div>
            <div
              class="avatar-content rounded-circle d-flex align-items-center justify-content-center"
              :class="`bg-${stat.variant}`"
              style="width: 48px; height: 48px"
            >
              <feather-icon
                :icon="stat.icon"
                size="24"
                :class="`text-${stat.textVariant}`"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Charts Row -->
    <b-row class="match-height">
      <!-- Trend Chart -->
      <b-col md="6">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <b-card-title>District vs State Proficiency Trend</b-card-title>
          </b-card-header>
          <b-card-body>
            <vue-apex-charts
              type="line"
              height="300"
              :options="trendChartOptions"
              :series="trendChartSeries"
            />
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Subject Performance Chart -->
      <b-col md="6">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <b-card-title>Subject Performance by School Level</b-card-title>
          </b-card-header>
          <b-card-body>
            <vue-apex-charts
              type="bar"
              height="300"
              :options="subjectLevelChartOptions"
              :series="subjectLevelChartSeries"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- School Performance Comparison -->
    <b-card no-body class="mt-2">
      <b-card-header>
        <b-card-title>School Performance Comparison</b-card-title>
      </b-card-header>
      <b-card-body>
        <div
          v-for="(school, index) in schoolPerformance"
          :key="index"
          class="d-flex flex-column flex-md-row justify-content-between align-items-md-center p-2 mb-2 border rounded shadow-sm hover-card"
        >
          <!-- Left: Info -->
          <div class="d-flex align-items-center mb-1 mb-md-0">
            <div
              class="rounded d-flex align-items-center justify-content-center bg-light text-dark mr-2"
              style="width: 48px; height: 48px"
            >
              <feather-icon icon="HomeIcon" size="24" />
            </div>
            <div>
              <h5 class="mb-0 font-weight-bold text-dark">{{ school.name }}</h5>
              <small>{{ school.students }} students</small>
            </div>
          </div>

          <!-- Right: Metrics -->
          <div
            class="d-flex align-items-center flex-wrap justify-content-end flex-grow-1"
          >
            <!-- Proficiency -->
            <div
              class="d-flex flex-column align-items-end mr-3"
              style="min-width: 150px"
            >
              <div class="d-flex justify-content-between w-100 mb-25">
                <small>Proficiency</small>
                <span class="font-weight-bold text-dark"
                  >{{ school.proficiency }}%</span
                >
              </div>
              <b-progress
                :value="school.proficiency"
                max="100"
                height="6px"
                variant="dark"
                class="w-100"
              ></b-progress>
            </div>

            <!-- Growth -->
            <div class="d-flex flex-column align-items-end mr-3">
              <small class="mb-25">Growth</small>
              <span class="font-weight-bold text-success">{{
                school.growth
              }}</span>
            </div>

            <!-- Status -->
            <b-badge
              pill
              :variant="school.badgeVariant"
              class="px-1 py-50"
              :class="school.status === 'On Track' ? 'text-white' : ''"
              :style="
                school.status === 'On Track' ? 'background-color: #1e1e2d' : ''
              "
            >
              {{ school.status }}
            </b-badge>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BAvatar,
  BProgress,
  BBadge,
  BButton,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import vSelect from "vue-select";

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BAvatar,
    BProgress,
    BBadge,
    BButton,
    VueApexCharts,
    vSelect,
  },
  data() {
    return {
      stats: [
        {
          title: "Total Schools",
          value: "12",
          icon: "HomeIcon",
          variant: "light",
          textVariant: "dark",
        },
        {
          title: "Total Students",
          value: "4,892",
          icon: "UsersIcon",
          variant: "light-info",
          textVariant: "info",
        },
        {
          title: "District Proficiency",
          value: "71%",
          icon: "AwardIcon",
          variant: "light-success",
          textVariant: "success",
        },
        {
          title: "YoY Growth",
          value: "+4.2%",
          valueClass: "text-success",
          icon: "TrendingUpIcon",
          variant: "light-success",
          textVariant: "success",
        },
      ],
      // Trend Chart
      trendChartSeries: [
        { name: "District", data: [65, 62, 69, 72, 74] },
        { name: "State", data: [68, 64, 70, 72, 73] },
      ],
      trendChartOptions: {
        chart: { type: "line", toolbar: { show: false } },
        colors: ["#1e1e2d", "#a0a0a0"],
        stroke: { curve: "smooth", width: [3, 2], dashArray: [0, 5] },
        markers: { size: 4, hover: { size: 6 } },
        xaxis: {
          categories: ["2020", "2021", "2022", "2023", "2024"],
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { min: 55, max: 80, tickAmount: 5 },
        grid: { borderColor: "#f1f1f1" },
        legend: { position: "bottom" },
      },
      // Subject Level Grouped Bar
      subjectLevelChartSeries: [
        { name: "Elementary", data: [72, 75, 70, 68] },
        { name: "Middle", data: [69, 73, 68, 66] },
        { name: "High", data: [74, 77, 72, 71] },
      ],
      subjectLevelChartOptions: {
        chart: { type: "bar", toolbar: { show: false } },
        colors: ["#1e1e2d", "#00cfe8", "#28c76f"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
          },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["Math", "ELA", "Science", "Social"],
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { min: 60, max: 80 },
        fill: { opacity: 1 },
        legend: { position: "bottom" },
        grid: { borderColor: "#f1f1f1" },
      },
      schoolPerformance: [
        {
          name: "Lincoln Elementary",
          students: "487",
          proficiency: 74,
          growth: "+4.2%",
          status: "On Track",
          badgeVariant: "dark",
        },
        {
          name: "Washington Middle",
          students: "623",
          proficiency: 71,
          growth: "+3.8%",
          status: "On Track",
          badgeVariant: "dark",
        },
        {
          name: "Jefferson Elementary",
          students: "412",
          proficiency: 68,
          growth: "+5.1%",
          status: "Improving",
          badgeVariant: "light-primary",
        },
        {
          name: "Roosevelt High",
          students: "892",
          proficiency: 76,
          growth: "+2.9%",
          status: "On Track",
          badgeVariant: "dark",
        },
        {
          name: "Adams Elementary",
          students: "356",
          proficiency: 62,
          growth: "+1.2%",
          status: "Needs Support",
          badgeVariant: "danger",
        },
        {
          name: "Madison Middle",
          students: "534",
          proficiency: 73,
          growth: "+4.5%",
          status: "On Track",
          badgeVariant: "dark",
        },
      ],
    };
  },
};
</script>

<style scoped>
.text-success {
  color: #28c76f !important;
}
.hover-card {
  transition: all 0.2s ease-in-out;
}
.hover-card:hover {
  transform: translateY(-2px);
}
</style>
